import type { 
  StrategyListItemApi, 
  StrategyDetailApi, 
  StrategyActionApi,
  TriggerConfigApi,
  AssignmentRulesApi,
  AIBehaviorApi,
  PerformanceMetricsApi,
  CreateStrategyRequest,
  CreateStrategyActionRequest,
  UpdateStrategyRequest,
} from "@/types/api/strategies";
import type { 
  CollectionStrategyItem, 
  StrategyAction, 
  TriggerConfig, 
  AssignmentRule, 
  AIBehaviorSettings, 
  StrategyPerformanceMetrics,
  AutomationDays,
} from "@/types/collection-strategies";

export function mapStrategyListItemApiToUi(api: StrategyListItemApi): CollectionStrategyItem {
  return {
    id: String(api.id),
    name: api.name,
    type: mapStrategyType(api.type),
    description: api.description ?? "",
    status: api.status === "Active" ? "Active" : "Inactive",
    openCasesCount: api.openCasesCount,
    avgDaysOpen: api.avgDaysOpen ?? 0,
    trigger: { type: "days_overdue", daysOverdue: 0 },
    actions: [],
    assignmentRules: [],
    aiBehavior: undefined,
    performanceMetrics: {
      totalCasesProcessed: 0,
      successfulResolutions: 0,
      averageResolutionTime: 0,
      collectionRate: 0,
    },
    createdAt: new Date(api.createdAt),
    updatedAt: new Date(api.updatedAt),
  };
}

export function mapStrategyDetailApiToUi(api: StrategyDetailApi): CollectionStrategyItem {
  return {
    id: String(api.id),
    name: api.name,
    type: mapStrategyType(api.type),
    description: api.description ?? "",
    status: api.status === "Active" ? "Active" : "Inactive",
    openCasesCount: api.openCasesCount,
    avgDaysOpen: api.avgDaysOpen ?? 0,
    trigger: mapTriggerConfig(api.triggerConfig),
    actions: api.actions.map(mapStrategyAction),
    assignmentRules: mapAssignmentRules(api.assignmentRules),
    aiBehavior: mapAIBehavior(api.aiBehavior),
    performanceMetrics: mapPerformanceMetrics(api.performanceMetrics),
    createdAt: new Date(api.createdAt),
    updatedAt: new Date(api.updatedAt),
    automationTime: api.automationTime ?? undefined,
    automationDays: mapAutomationDays(api),
    multiStageHandling: api.multiStageHandling ?? undefined,
  };
}

function mapStrategyType(type: string): CollectionStrategyItem["type"] {
  const typeMap: Record<string, CollectionStrategyItem["type"]> = {
    "standard": "Standard",
    "intensive": "Intensive",
    "vip": "VIP",
    "high risk": "High Risk",
    "highrisk": "High Risk",
  };
  return typeMap[type.toLowerCase()] ?? "Standard";
}

function mapStrategyAction(api: StrategyActionApi): StrategyAction {
  return {
    id: String(api.id),
    type: mapActionType(api.actionType),
    timing: {
      type: mapTimingType(api.timingType),
      daysFromTrigger: api.daysFromTrigger,
      daysFromPrevious: api.daysFromPrevious,
    },
    templateName: api.templateName,
    details: api.details,
    order: api.sortOrder,
    absoluteDayOffset: api.absoluteDayOffset,
  };
}

function mapActionType(type: string): StrategyAction["type"] {
  const typeMap: Record<string, StrategyAction["type"]> = {
    "email": "email",
    "phone_call": "phone_call",
    "payment_link": "payment_link",
    "sms": "sms",
    "escalation": "escalation",
    "account_action": "account_action",
    "payment_plan": "payment_plan",
  };
  return typeMap[type.toLowerCase()] ?? "email";
}

function mapTimingType(type: string): StrategyAction["timing"]["type"] {
  const typeMap: Record<string, StrategyAction["timing"]["type"]> = {
    "days_from_trigger": "days_from_trigger",
    "days_from_previous": "days_from_previous",
    "conditional": "conditional",
  };
  return typeMap[type.toLowerCase()] ?? "days_from_trigger";
}

function mapTriggerConfig(api?: TriggerConfigApi): TriggerConfig {
  if (!api) {
    return { type: "days_overdue", daysOverdue: 0 };
  }
  return {
    type: api.type as TriggerConfig["type"],
    daysOverdue: api.daysOverdue,
    amountThreshold: api.amountThreshold,
    riskScoreThreshold: api.riskScoreThreshold,
  };
}

function mapAssignmentRules(api?: AssignmentRulesApi): AssignmentRule[] {
  if (!api?.rules) {
    return [];
  }
  return api.rules.map((rule) => ({
    criterion: rule.criterion as AssignmentRule["criterion"],
    enabled: rule.enabled,
    config: rule.config,
  }));
}

function mapAIBehavior(api?: AIBehaviorApi): AIBehaviorSettings | undefined {
  if (!api) {
    return undefined;
  }
  return {
    enabled: api.enabled,
    communicationTone: (api.communicationTone ?? 3) as AIBehaviorSettings["communicationTone"],
    formalityLevel: (api.formalityLevel ?? "professional") as AIBehaviorSettings["formalityLevel"],
    personalizationDepth: (api.personalizationDepth ?? "moderate") as AIBehaviorSettings["personalizationDepth"],
    contextFactors: {
      paymentHistory: api.contextFactors?.paymentHistory ?? false,
      riskProfile: api.contextFactors?.riskProfile ?? false,
      relationshipDuration: api.contextFactors?.relationshipDuration ?? false,
      industryContext: api.contextFactors?.industryContext ?? false,
      economicFactors: api.contextFactors?.economicFactors ?? false,
      previousDisputes: api.contextFactors?.previousDisputes ?? false,
      communicationPreferences: api.contextFactors?.communicationPreferences ?? false,
    },
    timingOptimization: api.timingOptimization ?? false,
    escalationTriggers: {
      noResponseDays: api.escalationTriggers?.noResponseDays ?? 7,
      brokenPromises: api.escalationTriggers?.brokenPromises ?? 2,
      disputeRaised: api.escalationTriggers?.disputeRaised ?? false,
    },
    humanReviewRequired: api.humanReviewRequired ?? false,
  };
}

function mapPerformanceMetrics(api?: PerformanceMetricsApi): StrategyPerformanceMetrics {
  return {
    totalCasesProcessed: api?.totalCasesProcessed ?? 0,
    successfulResolutions: api?.successfulResolutions ?? 0,
    averageResolutionTime: api?.averageResolutionTime ?? 0,
    collectionRate: api?.collectionRate ?? 0,
    customerSatisfactionScore: api?.customerSatisfactionScore,
  };
}

export function mapStrategyUiToCreateRequest(
  strategy: Partial<CollectionStrategyItem>,
  strategyTypeId: number
): CreateStrategyRequest {
  return {
    name: strategy.name ?? "",
    strategyTypeId,
    description: strategy.description,
    status: strategy.status,
    triggerConfig: strategy.trigger ? {
      type: strategy.trigger.type,
      daysOverdue: strategy.trigger.daysOverdue,
      amountThreshold: strategy.trigger.amountThreshold,
      riskScoreThreshold: strategy.trigger.riskScoreThreshold,
    } : undefined,
    assignmentRules: strategy.assignmentRules?.length ? {
      rules: strategy.assignmentRules.map((rule) => ({
        criterion: rule.criterion,
        enabled: rule.enabled,
        config: rule.config,
      })),
    } : undefined,
    aiBehavior: strategy.aiBehavior ? mapAIBehaviorToApi(strategy.aiBehavior) : undefined,
    actions: strategy.actions?.map(mapActionToCreateRequest),
    automationTime: strategy.automationTime,
    automationMonday: strategy.automationDays?.monday,
    automationTuesday: strategy.automationDays?.tuesday,
    automationWednesday: strategy.automationDays?.wednesday,
    automationThursday: strategy.automationDays?.thursday,
    automationFriday: strategy.automationDays?.friday,
    automationSaturday: strategy.automationDays?.saturday,
    automationSunday: strategy.automationDays?.sunday,
    multiStageHandling: strategy.multiStageHandling,
  };
}

export function mapStrategyUiToUpdateRequest(
  strategy: Partial<CollectionStrategyItem>,
  strategyTypeId?: number
): UpdateStrategyRequest {
  return {
    name: strategy.name,
    strategyTypeId,
    description: strategy.description,
    status: strategy.status,
    triggerConfig: strategy.trigger ? {
      type: strategy.trigger.type,
      daysOverdue: strategy.trigger.daysOverdue,
      amountThreshold: strategy.trigger.amountThreshold,
      riskScoreThreshold: strategy.trigger.riskScoreThreshold,
    } : undefined,
    assignmentRules: strategy.assignmentRules?.length ? {
      rules: strategy.assignmentRules.map((rule) => ({
        criterion: rule.criterion,
        enabled: rule.enabled,
        config: rule.config,
      })),
    } : undefined,
    aiBehavior: strategy.aiBehavior ? mapAIBehaviorToApi(strategy.aiBehavior) : undefined,
    actions: strategy.actions?.map(mapActionToCreateRequest),
    automationTime: strategy.automationTime,
    automationMonday: strategy.automationDays?.monday,
    automationTuesday: strategy.automationDays?.tuesday,
    automationWednesday: strategy.automationDays?.wednesday,
    automationThursday: strategy.automationDays?.thursday,
    automationFriday: strategy.automationDays?.friday,
    automationSaturday: strategy.automationDays?.saturday,
    automationSunday: strategy.automationDays?.sunday,
    multiStageHandling: strategy.multiStageHandling,
  };
}

function mapAIBehaviorToApi(ai: AIBehaviorSettings): AIBehaviorApi {
  return {
    enabled: ai.enabled,
    communicationTone: ai.communicationTone,
    formalityLevel: ai.formalityLevel,
    personalizationDepth: ai.personalizationDepth,
    contextFactors: ai.contextFactors,
    timingOptimization: ai.timingOptimization,
    escalationTriggers: ai.escalationTriggers,
    humanReviewRequired: ai.humanReviewRequired,
  };
}

function mapActionToCreateRequest(action: StrategyAction): CreateStrategyActionRequest {
  return {
    actionTypeId: getActionTypeId(action.type),
    timingType: action.timing.type,
    daysFromTrigger: action.timing.daysFromTrigger,
    daysFromPrevious: action.timing.daysFromPrevious,
    templateName: action.templateName,
    details: action.details,
    sortOrder: action.order,
  };
}

function getActionTypeId(type: StrategyAction["type"]): number {
  const typeMap: Record<StrategyAction["type"], number> = {
    "email": 1,
    "phone_call": 2,
    "payment_link": 3,
    "sms": 4,
    "escalation": 5,
    "account_action": 6,
    "payment_plan": 7,
  };
  return typeMap[type] ?? 1;
}

export function getStrategyTypeId(type: CollectionStrategyItem["type"]): number {
  const typeMap: Record<CollectionStrategyItem["type"], number> = {
    "Standard": 1,
    "Intensive": 2,
    "VIP": 3,
    "High Risk": 4,
  };
  return typeMap[type] ?? 1;
}

function mapAutomationDays(api: StrategyDetailApi): AutomationDays | undefined {
  if (
    api.automationMonday === undefined &&
    api.automationTuesday === undefined &&
    api.automationWednesday === undefined &&
    api.automationThursday === undefined &&
    api.automationFriday === undefined &&
    api.automationSaturday === undefined &&
    api.automationSunday === undefined
  ) {
    return undefined;
  }
  return {
    monday: api.automationMonday ?? false,
    tuesday: api.automationTuesday ?? false,
    wednesday: api.automationWednesday ?? false,
    thursday: api.automationThursday ?? false,
    friday: api.automationFriday ?? false,
    saturday: api.automationSaturday ?? false,
    sunday: api.automationSunday ?? false,
  };
}
